/* ==========================================================================
   ATAARA HERO — slideshow controller (ES6, no globals, no dependencies)
   ========================================================================== */
(function () {
  'use strict';

  const SWIPE_THRESHOLD = 45;
  const DIRECTION_LOCK = 8;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class AtaaraHero extends HTMLElement {
    constructor() {
      super();
      this._onPointerDown = this._onPointerDown.bind(this);
      this._onPointerMove = this._onPointerMove.bind(this);
      this._onPointerUp = this._onPointerUp.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
      this._onMouseEnter = this._onMouseEnter.bind(this);
      this._onMouseLeave = this._onMouseLeave.bind(this);
      this._onFocusIn = this._onFocusIn.bind(this);
      this._onFocusOut = this._onFocusOut.bind(this);
      this._onVisibility = this._onVisibility.bind(this);
      this._onReducedMotionChange = this._onReducedMotionChange.bind(this);
      this._onBlockSelect = this._onBlockSelect.bind(this);
      this._onBlockDeselect = this._onBlockDeselect.bind(this);
      this._onResize = this._onResize.bind(this);
      this._syncHeaderOffsets = this._syncHeaderOffsets.bind(this);
      this._tick = this._tick.bind(this);
    }

    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.slides = Array.from(this.querySelectorAll('[data-ataara-slide]'));
      if (this.slides.length === 0) return;

      this.viewport = this.querySelector('.ataara-hero__viewport');
      this.dots = Array.from(this.querySelectorAll('[data-ataara-dot]'));
      this.prevBtn = this.querySelector('[data-ataara-prev]');
      this.nextBtn = this.querySelector('[data-ataara-next]');
      this.toggleBtn = this.querySelector('[data-ataara-toggle]');
      this.liveRegion = this.querySelector('.ataara-hero__live');

      this.config = {
        autoplay: this.dataset.autoplay === 'true',
        interval: parseInt(this.dataset.interval, 10) || 6000,
        transition: parseInt(this.dataset.transition, 10) || 900,
        pauseOnHover: this.dataset.pauseOnHover === 'true',
        swipe: this.dataset.swipe === 'true',
        keyboard: this.dataset.keyboard === 'true'
      };

      this.total = this.slides.length;
      this.index = this.slides.findIndex((s) => s.classList.contains('is-active'));
      if (this.index < 0) this.index = 0;

      this._headerLayout = this.dataset.headerLayout || 'below';
      this._syncHeaderOffsets();

      this._rafId = null;
      this._elapsed = 0;
      this._lastTime = null;
      this._pausedBy = new Set();
      this._motionOK = !reducedMotion.matches;

      this._bindEvents();
      this.classList.add('is-loaded');

      if (this.config.autoplay && this.total > 1 && this._motionOK) {
        this._startAutoplay();
      } else {
        this._reflectPlayState(false);
      }

      if ('IntersectionObserver' in window && this.total > 1) {
        this._io = new IntersectionObserver((entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              this._resume('offscreen');
            } else {
              this._pause('offscreen');
            }
          }
        }, { threshold: 0.15 });
        this._io.observe(this);
      }
    }

    disconnectedCallback() {
      this._stopAutoplay();
      this._unbindEvents();
      if (this._io) { this._io.disconnect(); this._io = null; }
      window.clearTimeout(this._resizeTimer);
      this._initialized = false;
    }

    _bindEvents() {
      if (this.dots.length) {
        this.dots.forEach((dot) => {
          dot.addEventListener('click', () => {
            const i = parseInt(dot.dataset.ataaraDot, 10);
            this._userGoTo(i);
          });
        });
      }
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this._userGoTo(this._wrap(this.index - 1)));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this._userGoTo(this._wrap(this.index + 1)));
      if (this.toggleBtn) this.toggleBtn.addEventListener('click', () => this._toggleAutoplay());

      if (this.config.pauseOnHover) {
        this.addEventListener('mouseenter', this._onMouseEnter);
        this.addEventListener('mouseleave', this._onMouseLeave);
      }

      this.addEventListener('focusin', this._onFocusIn);
      this.addEventListener('focusout', this._onFocusOut);

      if (this.config.keyboard) {
        this.addEventListener('keydown', this._onKeydown);
      }

      if (this.config.swipe && this.viewport && window.PointerEvent) {
        this.viewport.addEventListener('pointerdown', this._onPointerDown);
      }

      window.addEventListener('resize', this._onResize, { passive: true });
      window.addEventListener('load', this._syncHeaderOffsets);
      document.addEventListener('visibilitychange', this._onVisibility);

      if (typeof reducedMotion.addEventListener === 'function') {
        reducedMotion.addEventListener('change', this._onReducedMotionChange);
      } else if (typeof reducedMotion.addListener === 'function') {
        reducedMotion.addListener(this._onReducedMotionChange);
      }

      document.addEventListener('shopify:block:select', this._onBlockSelect);
      document.addEventListener('shopify:block:deselect', this._onBlockDeselect);
    }

    _unbindEvents() {
      this.removeEventListener('mouseenter', this._onMouseEnter);
      this.removeEventListener('mouseleave', this._onMouseLeave);
      this.removeEventListener('focusin', this._onFocusIn);
      this.removeEventListener('focusout', this._onFocusOut);
      this.removeEventListener('keydown', this._onKeydown);
      if (this.viewport) {
        this.viewport.removeEventListener('pointerdown', this._onPointerDown);
      }
      window.removeEventListener('pointermove', this._onPointerMove);
      window.removeEventListener('pointerup', this._onPointerUp);
      window.removeEventListener('resize', this._onResize);
      window.removeEventListener('load', this._syncHeaderOffsets);
      document.removeEventListener('visibilitychange', this._onVisibility);
      if (typeof reducedMotion.removeEventListener === 'function') {
        reducedMotion.removeEventListener('change', this._onReducedMotionChange);
      } else if (typeof reducedMotion.removeListener === 'function') {
        reducedMotion.removeListener(this._onReducedMotionChange);
      }
      document.removeEventListener('shopify:block:select', this._onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this._onBlockDeselect);
    }

    _wrap(i) {
      return (i + this.total) % this.total;
    }

    _goTo(next) {
      if (next === this.index || next < 0 || next >= this.total) {
        if (next === this.index) this._restartProgress();
        return;
      }

      const current = this.slides[this.index];
      const target = this.slides[next];

      current.classList.remove('is-active');
      current.classList.add('is-leaving');
      current.setAttribute('aria-hidden', 'true');
      current.setAttribute('inert', '');
      this._setFocusable(current, false);

      window.clearTimeout(this._leaveTimer);
      this._leaveTimer = window.setTimeout(() => {
        current.classList.remove('is-leaving');
      }, this.config.transition);

      target.classList.add('is-active');
      target.removeAttribute('aria-hidden');
      target.removeAttribute('inert');
      this._setFocusable(target, true);

      this.index = next;
      this._updateDots();
      this._announce();
      this._restartProgress();
    }

    _userGoTo(i) {
      this._goTo(i);
      this._elapsed = 0;
      this._lastTime = null;
    }

    _setFocusable(slide, focusable) {
      const link = slide.querySelector('.ataara-slide__button');
      if (link) {
        if (focusable) link.removeAttribute('tabindex');
        else link.setAttribute('tabindex', '-1');
      }
    }

    _updateDots() {
      if (!this.dots.length) return;
      this.dots.forEach((dot, i) => {
        const active = i === this.index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
        if (active) dot.removeAttribute('tabindex');
        else dot.setAttribute('tabindex', '-1');
      });
    }

    _announce() {
      if (!this.liveRegion) return;
      const slide = this.slides[this.index];
      const heading = slide.querySelector('.ataara-slide__heading');
      const label = heading ? heading.textContent.trim() : '';
      this.liveRegion.textContent =
        'Slide ' + (this.index + 1) + ' of ' + this.total + (label ? ': ' + label : '');
    }

    _restartProgress() {
      if (!this.dots.length) return;
      const dot = this.dots[this.index];
      if (!dot) return;
      const fill = dot.querySelector('.ataara-hero__dot-fill');
      if (!fill) return;
      fill.style.animation = 'none';
      void fill.offsetWidth;
      fill.style.animation = '';
    }

    _startAutoplay() {
      if (!this.config.autoplay || this.total < 2 || !this._motionOK) return;
      this._pausedBy.clear();
      this._elapsed = 0;
      this._lastTime = null;
      this._reflectPlayState(true);
      this._rafId = window.requestAnimationFrame(this._tick);
    }

    _stopAutoplay() {
      if (this._rafId) {
        window.cancelAnimationFrame(this._rafId);
        this._rafId = null;
      }
      window.clearTimeout(this._leaveTimer);
    }

    _tick(now) {
      if (this._pausedBy.size > 0) { this._rafId = null; return; }
      if (this._lastTime === null) this._lastTime = now;
      this._elapsed += now - this._lastTime;
      this._lastTime = now;

      if (this._elapsed >= this.config.interval) {
        this._elapsed = 0;
        this._goTo(this._wrap(this.index + 1));
      }
      this._rafId = window.requestAnimationFrame(this._tick);
    }

    _pause(reason) {
      const wasRunning = this._pausedBy.size === 0;
      this._pausedBy.add(reason);
      if (wasRunning) {
        if (this._rafId) { window.cancelAnimationFrame(this._rafId); this._rafId = null; }
        this._lastTime = null;
        this._reflectPlayState(false);
      }
    }

    _resume(reason) {
      if (!this._pausedBy.has(reason)) return;
      this._pausedBy.delete(reason);
      if (this._pausedBy.size === 0 && this._userPaused !== true &&
          this.config.autoplay && this.total > 1 && this._motionOK) {
        this._lastTime = null;
        this._reflectPlayState(true);
        if (!this._rafId) this._rafId = window.requestAnimationFrame(this._tick);
      }
    }

    _toggleAutoplay() {
      if (this._userPaused) {
        this._userPaused = false;
        this._resume('user');
      } else {
        this._userPaused = true;
        this._pause('user');
      }
    }

    _reflectPlayState(playing) {
      this.classList.toggle('is-playing', playing);
      this.classList.toggle('is-paused', !playing);
      if (this.toggleBtn) {
        this.toggleBtn.setAttribute('aria-label', playing ? 'Pause slideshow' : 'Play slideshow');
      }
      if (playing) this._restartProgress();
    }

    _onMouseEnter() { this._pause('hover'); }
    _onMouseLeave() { this._resume('hover'); }

    _onFocusIn() { this._pause('focus'); }
    _onFocusOut(e) {
      if (!this.contains(e.relatedTarget)) this._resume('focus');
    }

    _onVisibility() {
      if (document.hidden) this._pause('tab');
      else this._resume('tab');
    }

    _onKeydown(e) {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this._userGoTo(this._wrap(this.index - 1));
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this._userGoTo(this._wrap(this.index + 1));
      }
    }

    _onReducedMotionChange(e) {
      this._motionOK = !e.matches;
      if (!this._motionOK) {
        this._pause('reduced-motion');
      } else {
        this._resume('reduced-motion');
        if (this.config.autoplay && !this._rafId && this._pausedBy.size === 0) {
          this._startAutoplay();
        }
      }
    }

    _onPointerDown(e) {
      if (!e.isPrimary || (e.pointerType === 'mouse' && e.button !== 0)) return;
      this._pointerId = e.pointerId;
      this._startX = e.clientX;
      this._startY = e.clientY;
      this._deltaX = 0;
      this._locked = null;
      this._dragging = true;
      this._pause('drag');
      window.addEventListener('pointermove', this._onPointerMove, { passive: false });
      window.addEventListener('pointerup', this._onPointerUp);
      window.addEventListener('pointercancel', this._onPointerUp);
    }

    _onPointerMove(e) {
      if (!this._dragging || e.pointerId !== this._pointerId) return;
      const dx = e.clientX - this._startX;
      const dy = e.clientY - this._startY;

      if (this._locked === null) {
        if (Math.abs(dx) > DIRECTION_LOCK || Math.abs(dy) > DIRECTION_LOCK) {
          this._locked = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        }
      }

      if (this._locked === 'x') {
        e.preventDefault();
        this._deltaX = dx;
      }
    }

    _onPointerUp(e) {
      if (!this._dragging || e.pointerId !== this._pointerId) return;
      this._dragging = false;
      window.removeEventListener('pointermove', this._onPointerMove);
      window.removeEventListener('pointerup', this._onPointerUp);
      window.removeEventListener('pointercancel', this._onPointerUp);

      if (this._locked === 'x' && Math.abs(this._deltaX) >= SWIPE_THRESHOLD && this.total > 1) {
        if (this._deltaX < 0) this._userGoTo(this._wrap(this.index + 1));
        else this._userGoTo(this._wrap(this.index - 1));
      }
      this._resume('drag');
    }

    _onBlockSelect(e) {
      if (!this.contains(e.target)) return;
      const slide = e.target.closest('[data-ataara-slide]');
      if (!slide) return;
      const i = this.slides.indexOf(slide);
      if (i >= 0) {
        this._pause('editor');
        this._goTo(i);
      }
    }

    _onBlockDeselect(e) {
      if (!this.contains(e.target)) return;
      this._resume('editor');
    }

    _findHeader() {
      const selectors = [
        '.shopify-section-group-header-group',
        '#shopify-section-header',
        'sticky-header',
        '.section-header',
        '.site-header',
        '.header-wrapper',
        'header[role="banner"]',
        'header.header',
        'header'
      ];
      const heroTop = this.getBoundingClientRect().top;
      for (const sel of selectors) {
        const el = document.querySelector(sel);
        if (!el || !el.offsetHeight) continue;
        const pos = window.getComputedStyle(el).position;
        const overlays = pos === 'fixed' || pos === 'absolute' || pos === 'sticky';
        const sitsAbove = el.getBoundingClientRect().bottom <= heroTop + 4;
        if (overlays || sitsAbove) return el;
      }
      return null;
    }

    _syncHeaderOffsets() {
      const fallback = parseInt(this.dataset.headerHeightFallback, 10) || 0;
      const header = this._findHeader();
      let offset = fallback;
      let push = 0;

      if (header) {
        offset = Math.round(header.offsetHeight) || fallback;
        if (window.scrollY < 4) {
          const covered = header.getBoundingClientRect().bottom - this.getBoundingClientRect().top;
          push = Math.max(0, Math.round(covered));
          this._lastPush = push;
        } else if (typeof this._lastPush === 'number') {
          push = this._lastPush;
        }
      }

      this.style.setProperty('--ataara-header-offset', offset + 'px');
      this.style.setProperty(
        '--ataara-header-push',
        (this._headerLayout === 'below' ? push : 0) + 'px'
      );
    }

    _onResize() {
      window.clearTimeout(this._resizeTimer);
      this._resizeTimer = window.setTimeout(this._syncHeaderOffsets, 150);
    }
  }

  if (!customElements.get('ataara-hero')) {
    customElements.define('ataara-hero', AtaaraHero);
  }
})();